import React from 'react';
import _ from 'lodash';

import { PAGE, SECTION, SECTION_COLUMN, CONTAINER, CAROUSEL, CAROUSEL_CARD } from 'constants/widgetTypes';
import { updateWidgetProperty } from 'actions/editor';
import { getCardFlexConfig } from '../carousel/card/flexConfig';
import {
  capPaddingSides,
  LAYOUT_TYPE_FLEX,
  LAYOUT_TYPE_FREEFORM,
  ownLayoutType,
  resolveFlexEnabled,
} from './flexCss';

export {
  LAYOUT_TYPE_FLEX,
  LAYOUT_TYPE_FREEFORM,
  normalizeLayoutType,
  ownLayoutType,
  resolveFlexEnabled,
  flexValueToCss,
  pxToNumber,
  numberToPx,
  resolvePadding,
  paddingToStored,
  paddingToCss,
  capPaddingSides,
  MOBILE_MAX_PADDING,
} from './flexCss';

export const DEFAULT_FLEX_LAYOUT = {
  flexDirection: 'row',
  justifyContent: 'flexStart',
  alignItems: 'flexStart',
  alignContent: 'flexStart',
  flexWrap: 'wrap',
  gap: '0px',
  padding: { topLeft: '0px', topRight: '0px', bottomLeft: '0px', bottomRight: '0px', lock: true }
};

export const PAGE_DEFAULT_FLEX_LAYOUT = {
  ...DEFAULT_FLEX_LAYOUT,
  alignItems: 'flexStart'
};

export const MOBILE_FLEX_DEFAULTS = {
  flexDirection: 'column'
};

export function resolveFlexLayout(widgetProps, breakpoint, baseDefaults = DEFAULT_FLEX_LAYOUT) {
  const stored = bp => _.omitBy(_.get(widgetProps, `properties.${bp}.flexProperty`), _.isNil);
  const desktop = stored('desktop');
  if (breakpoint === 'mobile') {
    const mobile = stored('mobile');
    const resolved = _.defaults({}, mobile, MOBILE_FLEX_DEFAULTS, desktop, baseDefaults);
    if (_.get(mobile, 'padding') == null) {
      resolved.padding = capPaddingSides(resolved.padding);
    }
    return resolved;
  }
  return _.defaults({}, desktop, baseDefaults);
}

export const FlexFlowContext = React.createContext({ flexMode: false, flexLayout: DEFAULT_FLEX_LAYOUT });

const FLEX_CONTAINERS = {
  [PAGE]: { baseDefaults: PAGE_DEFAULT_FLEX_LAYOUT },
  [SECTION_COLUMN]: { baseDefaults: DEFAULT_FLEX_LAYOUT },
  [CONTAINER]: { baseDefaults: DEFAULT_FLEX_LAYOUT },
  [CAROUSEL_CARD]: { baseDefaults: DEFAULT_FLEX_LAYOUT },
};

export const PASSES_FLEX_FLOW_THROUGH = [SECTION, CAROUSEL];

export function isFlexContainerType(type) {
  return _.has(FLEX_CONTAINERS, type);
}

export { breaksOutOfFlexFlow } from './flexFlowRegistry';

export function isCascadeFlexTarget(type) {
  return isFlexContainerType(type) || type === CAROUSEL_CARD;
}

export function flexBaseDefaults(type) {
  return _.get(FLEX_CONTAINERS, [type, 'baseDefaults'], DEFAULT_FLEX_LAYOUT);
}

export function resolveFlexMinHeight(widgetProps, breakpoint, baseDefaults = DEFAULT_FLEX_LAYOUT) {
  const resolved = resolveFlexLayout(widgetProps, breakpoint, baseDefaults);
  return parseFloat(_.get(resolved, 'minHeight', 0)) || 0;
}

export function resolveFlexMinHeightSource(widgetProps, breakpoint) {
  return _.get(widgetProps, `properties.${breakpoint}.flexProperty.minHeightSource`, null);
}

export function useFlexMinHeightCapture({
  editor, id, breakpoint, flexMode, dispatch,
  getElement = (root) => root,
  resolveFloor, widgetProps, deps = [],
}) {
  const lastCommittedRef = React.useRef({});

  // Keep the latest widgetProps reachable from the long-lived ResizeObserver
  // callbacks below — otherwise they read a stale closure and can clobber a
  // just-committed user minHeight with an auto re-measurement.
  const widgetPropsRef = React.useRef(widgetProps);
  widgetPropsRef.current = widgetProps;

  React.useEffect(() => {
    if (!flexMode) return;

    const oppositeBreakpoint = breakpoint === 'desktop' ? 'mobile' : 'desktop';

    const canvasRoot = document.querySelector(`.editor_widget_scroll_container [data-widget-id="${id}"]`)
      || document.getElementById(id);
    const previewRoot = document.querySelector(`.editor_device_preview [data-widget-id="${id}"]`);
    const canvasEl = canvasRoot && getElement(canvasRoot);
    const previewEl = previewRoot && getElement(previewRoot);

    const isFloorResizing = () =>
      !!document.querySelector('[data-flex-floor-resizing="true"]');

    const measure = (el) => {
      if (!el) return 0;
      if (isFloorResizing()) return 0;
      // An empty flex container has no flowed content to record — its height is
      // purely user/default-driven, so auto-capture must not run (it would just
      // persist the empty-state floor and clobber a user-set minHeight).
      if (!el.querySelector('[data-widget-id]')) return 0;
      const prevMinHeight = el.style.minHeight;
      el.style.minHeight = '0px';
      const rendered = Math.round(el.scrollHeight);
      el.style.minHeight = prevMinHeight;
      return rendered;
    };

    const commit = (bp, rendered) => {
      if (!rendered || rendered <= 0) return;

      if (isFloorResizing()) return;

      const liveProps = widgetPropsRef.current;

      if (resolveFlexMinHeightSource(liveProps, bp) === 'user') return;

      const own = Math.round(resolveFloor(liveProps, bp));
      if (own === rendered) return;

      if (lastCommittedRef.current[bp] === rendered) return;

      const ownFlexProperty = _.get(liveProps, `properties.${bp}.flexProperty`, {});
      lastCommittedRef.current[bp] = rendered;
      dispatch(updateWidgetProperty(editor, id, bp, {
        flexProperty: { ...ownFlexProperty, minHeight: `${rendered}px`, minHeightSource: 'auto' },
      }));
    };

    const captureCanvas = () => commit(breakpoint, measure(canvasEl));
    const capturePreview = () => commit(oppositeBreakpoint, measure(previewEl));

    const raf = requestAnimationFrame(() => requestAnimationFrame(() => {
      captureCanvas();
      capturePreview();
    }));

    const observers = [];
    if (canvasEl) {
      const o = new ResizeObserver(captureCanvas);
      o.observe(canvasEl);
      observers.push(o);
    }
    if (previewEl) {
      const o = new ResizeObserver(capturePreview);
      o.observe(previewEl);
      observers.push(o);
    }

    return () => {
      cancelAnimationFrame(raf);
      observers.forEach(o => o.disconnect());
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [flexMode, breakpoint, id, ...deps]);
}

// Stored flexProperty can hold explicit nulls (e.g. `justifyContent: null`) for
// fields the user never touched. Rendering falls back to defaults, but the API
// must receive the values the widget actually renders with, so every flowing
// breakpoint gets its fully-resolved flexProperty written back.
function resolvedFlexPropertyForSave(widget, breakpoint, parentFlex) {
  const type = _.get(widget, 'type');
  if (type === CAROUSEL_CARD) {
    const config = getCardFlexConfig(widget, breakpoint, parentFlex);
    return _.get(config, 'isFlexLayout') ? config.flexProperty : null;
  }
  if (isFlexContainerType(type) && resolveFlexEnabled(widget, breakpoint, parentFlex)) {
    return resolveFlexLayout(widget, breakpoint, flexBaseDefaults(type));
  }
  return null;
}

export function fillFlexDefaults(widget, parentFlex = false) {
  return ['desktop', 'mobile'].reduce((acc, breakpoint) => {
    const resolved = resolvedFlexPropertyForSave(acc, breakpoint, parentFlex);
    if (!resolved) return acc;
    return {
      ...acc,
      properties: {
        ...acc.properties,
        [breakpoint]: {
          ...(acc.properties && acc.properties[breakpoint]),
          flexProperty: resolved,
        },
      },
    };
  }, widget);
}

export function flowsChildrenInFlex(type, widgetProps, breakpoint = 'desktop', parentFlex = false) {
  return isFlexContainerType(type) && resolveFlexEnabled(widgetProps, breakpoint, parentFlex);
}

export function allColumnsInFlexMode(columns, parentFlex = false) {
  return columns.length > 0
    && columns.every(column => flowsChildrenInFlex(column.type, column, 'desktop', parentFlex));
}

export const CONTENT_DRIVEN_SECTION_HEIGHT = 'fit-content';

export const EMPTY_CONTENT_SECTION_MIN_HEIGHT = 200;

export function isStretchedWidget(widget) {
  return _.get(widget, 'properties.desktop.stretched', false);
}

function widgetLookup(source) {
  if (!source) return () => undefined;
  if (typeof source.get === 'function') return uid => source.get(uid);
  if (_.isArray(source)) {
    const byId = _.keyBy(source, 'uid');
    return uid => byId[uid];
  }
  return uid => source[uid];
}

// Resolves a widget's flex state against its real ancestor chain. Used outside
// the React tree (reducers, moveable configs, toolbars) where FlexFlowContext
// isn't available. Walks up to the nearest flex-container ancestor, since only
// those flow their children; a widget whose own mode is explicit short-circuits.
export function resolveFlexEnabledInTree(source, widget, breakpoint = 'desktop') {
  if (!widget) return false;
  const lookup = widgetLookup(source);

  const chain = [];
  let current = widget;
  const seen = new Set();
  while (current && !seen.has(current.uid)) {
    seen.add(current.uid);
    chain.push(current);
    if (ownLayoutType(current, breakpoint) !== null) break;
    current = lookup(current.parent);
  }

  // Walk back down from the outermost ancestor, carrying the resolved value.
  let flex = false;
  for (let i = chain.length - 1; i >= 0; i -= 1) {
    const node = chain[i];
    const resolved = node.type === CAROUSEL_CARD
      ? _.get(getCardFlexConfig(node, breakpoint, flex), 'isFlexLayout', false)
      : resolveFlexEnabled(node, breakpoint, flex);
    // Only flex containers pass their flow down to children -- except SECTION,
    // which is structural (it just holds columns) and passes the flow through,
    // mirroring what the FlexFlowContext chain does in the React tree.

    flex = i === 0
      ? resolved
      : (PASSES_FLEX_FLOW_THROUGH.includes(node.type)
          ? flex
          : (resolved && isCascadeFlexTarget(node.type)));
  }
  return flex;
}

// Save-time materialisation of layoutType.
//
// On the canvas a widget's layoutType stays null so it keeps following its
// parent. The API must never receive null, so every flex-capable widget is
// resolved here to the explicit 'flex' / 'freeform' it currently renders as.
// A widget that already holds an explicit value is left untouched.
export function materialiseLayoutTypes(widgets) {
  if (!_.isArray(widgets) || widgets.length === 0) return widgets;
  const byId = _.keyBy(widgets, 'uid');

  return widgets.map(widget => {
    if (!isCascadeFlexTarget(widget.type)) return widget;
    // Already saved with a layoutType -- leave it untouched.
    if (_.get(widget, 'properties.desktop.layoutType') != null) return fillFlexDefaults(widget);

    // A legacy flexBoxEnabled value carries over as-is; otherwise the widget
    // takes what it currently renders as through inheritance. Either way it is
    // written as layoutType, so old pages migrate on their first save.
    const own = ownLayoutType(widget, 'desktop');
    const flowing = own !== null
      ? own === LAYOUT_TYPE_FLEX
      : resolveFlexEnabledInTree(byId, widget, 'desktop');
    // processRequest already ran fillFlexDefaults, but with no parent info an
    // inheriting widget resolved to freeform there and was skipped. Now that
    // layoutType is explicit, fill its desktop and mobile flexProperty.
    return fillFlexDefaults({
      ...widget,
      properties: {
        ...widget.properties,
        desktop: {
          ...(widget.properties && widget.properties.desktop),
          layoutType: flowing ? LAYOUT_TYPE_FLEX : LAYOUT_TYPE_FREEFORM,
        },
      },
    });
  });
}

// Inserted subtrees that land inside a flex flow need a minHeight floor, or a
// container with no content yet resolves to height 0 and collapses the parent.
// layoutType is deliberately NOT written here: it stays null on the canvas so
// the widget keeps following its parent. It is materialised at save time.
// The palette API stamps flexBoxEnabled: false on every new widget. With no
// layoutType, the legacy fallback would read that as an explicit 'freeform' and
// the widget would stop following its parent. So when a widget lands on the
// canvas with no layoutType and flexBoxEnabled false, the false is dropped and
// it inherits. A true value, or any layoutType, is left untouched.
function dropUnsetLegacyFlex(widget) {
  const desktop = _.get(widget, 'properties.desktop');
  if (!desktop || desktop.layoutType != null || desktop.flexBoxEnabled !== false) return widget;
  return {
    ...widget,
    properties: { ...widget.properties, desktop: _.omit(desktop, 'flexBoxEnabled') },
  };
}

export function resolveInsertedSubtrees(allWidgets, rootIds) {
  const childrenByParent = _.groupBy(allWidgets, 'parent');

  const subtree = new Set();
  const walk = (id) => {
    if (subtree.has(id)) return;
    subtree.add(id);
    (childrenByParent[id] || []).forEach(child => walk(child.uid));
  };
  (rootIds || []).forEach(walk);

  const widgets = allWidgets.map(widget =>
    subtree.has(widget.uid) && isCascadeFlexTarget(widget.type)
      ? dropUnsetLegacyFlex(widget)
      : widget);
  const byId = _.keyBy(widgets, 'uid');

  const toSeed = new Set();
  subtree.forEach(uid => {
    const widget = byId[uid];
    if (!widget || !isCascadeFlexTarget(widget.type)) return;
    if (_.get(widget, 'properties.desktop.flexProperty.minHeight') != null) return;
    if (!resolveFlexEnabledInTree(byId, widget, 'desktop')) return;
    toSeed.add(uid);
  });

  if (toSeed.size === 0) return widgets;

  return widgets.map(widget => toSeed.has(widget.uid)
    ? {
        ...widget,
        properties: {
          ...widget.properties,
          desktop: {
            ...(widget.properties && widget.properties.desktop),
            flexProperty: resolvedFlexPropertyFor(
              widget, 'desktop', (childrenByParent[widget.uid] || []).length > 0
            ),
          }
        }
      }
    : widget);
}

export function resolveCardFlexInTree(source, card, breakpoint = 'desktop') {
  if (!card) return false;
  const lookup = widgetLookup(source);
  const parentFlex = ownLayoutType(card, breakpoint) === null
    ? resolveFlexEnabledInTree(source, lookup(card.parent), breakpoint)
    : false;
  return _.get(getCardFlexConfig(card, breakpoint, parentFlex), 'isFlexLayout', false);
}

// True when the widget's parent flows it as a flex item.
export function isInFlexFlow(source, widget, breakpoint = 'desktop') {
  const lookup = widgetLookup(source);
  const parent = widget && lookup(widget.parent);
  if (!parent || !isCascadeFlexTarget(parent.type)) return false;
  return resolveFlexEnabledInTree(source, parent, breakpoint);
}

export const RESET_FLEX_CONTEXT = { flexMode: false, flexLayout: DEFAULT_FLEX_LAYOUT };

function descendantIds(widgets, rootId) {
  const childrenByParent = _.groupBy(widgets, 'parent');
  const result = [];
  const walk = (parentId) => {
    (childrenByParent[parentId] || []).forEach(child => {
      result.push(child.uid);
      walk(child.uid);
    });
  };
  walk(rootId);
  return result;
}

function eachDescendantFlexContainer(widgets, widgetId, fn) {
  const descendantSet = new Set(descendantIds(widgets, widgetId));
  widgets
    .filter(w => descendantSet.has(w.uid) && isCascadeFlexTarget(w.type))
    .forEach(fn);
}

export function flowedDescendants(widgets, rootId, breakpoint = 'desktop') {
  const subtree = new Set([rootId, ...descendantIds(widgets, rootId)]);
  const byId = _.keyBy(widgets, 'uid');
  return widgets.filter(w => {
    const parent = byId[w.parent];
    if (!parent || !subtree.has(parent.uid)) return false;
    return isFlexContainerType(parent.type)
      && resolveFlexEnabledInTree(byId, parent, breakpoint);
  });
}

export const EMPTY_FLEX_MIN_HEIGHT = 200;

function resolvedFlexPropertyFor(widget, breakpoint, hasChildren) {
  const ownFlexProperty = _.get(widget, `properties.${breakpoint}.flexProperty`, {});
  if (_.get(ownFlexProperty, 'minHeight') != null) return ownFlexProperty;

  const height = hasChildren
    ? (parseFloat(_.get(widget, `properties.${breakpoint}.layout.height`))
        || parseFloat(_.get(widget, 'properties.desktop.layout.height'))
        || 0)
    : EMPTY_FLEX_MIN_HEIGHT;
  if (height <= 0) return ownFlexProperty;

  return { ...ownFlexProperty, minHeight: `${Math.round(height)}px`, minHeightSource: 'auto' };
}

export function applyFlexModeToggle(dispatch, editorSession, widgets, widgetId, enabling, { cascade = false, capturedLayouts = [], breakpoint = 'desktop', flexModeOverridden = false, groupId = null } = {}) {
  const marker = flexModeOverridden ? { flexModeOverridden: true } : {};
  const byId = _.keyBy(widgets, 'uid');
  const hasChildren = (uid) => (widgets || []).some(w => w.parent === uid);

  if (!enabling) {
    capturedLayouts.forEach(({ uid, breakpoint: bp, layout }) => {
      dispatch(updateWidgetProperty(editorSession, uid, bp, { layout }, groupId));
    });

    dispatch(updateWidgetProperty(editorSession, widgetId, breakpoint, {
      layoutType: LAYOUT_TYPE_FREEFORM,
      ...marker,
    }, groupId));

    // Descendants keep their own layoutType. Ones left at null follow this
    // container automatically at read time; ones set explicitly to 'flex' or
    // 'freeform' made that choice themselves and must not be overwritten.
    return;
  }

  dispatch(updateWidgetProperty(editorSession, widgetId, breakpoint, {
    layoutType: LAYOUT_TYPE_FLEX,
    flexProperty: resolvedFlexPropertyFor(byId[widgetId], breakpoint, hasChildren(widgetId)),
    ...marker,
  }, groupId));

  // No cascade on enable: descendants left at null now inherit this container's
  // flex flow at read time. Descendants explicitly set to 'freeform' stay put.
  if (!cascade) return;

  // Seed flexProperty for descendants that will start flowing, so they have a
  // sane layout without being forced to an explicit mode.
  eachDescendantFlexContainer(widgets, widgetId, container => {
    if (ownLayoutType(container, 'desktop') !== null) return;
    if (_.get(container, 'properties.desktop.flexProperty')) return;
    dispatch(updateWidgetProperty(editorSession, container.uid, 'desktop', {
      flexProperty: resolvedFlexPropertyFor(container, 'desktop', hasChildren(container.uid)),
    }, groupId));
  });
}
